import get from 'lodash.get';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';

import { ERROR, IN_PROGRESS, SUCCESS } from 'components/StatusIcon/constants';
import { selectEntities } from 'services/entities/selectors';
import { selectProductsKeyedById } from 'services/products/selectors';
import { VirtualizedTableColumns } from 'services/virtualizedTable/types';
import { makeSelectTableColumnWidths } from 'services/columnWidths/selectors';
import {
    FIELD_TYPE_STRING,
    FIELD_TYPE_DATE,
    FIELD_TYPE_STATUS_ICON,
    FIELD_TYPE_CONFIRMATION_CHECKBOX
} from 'components/VirtualizedTable/constants';
import { selectSpraySubmissionArray } from 'services/spraySubmission/selectors';
import { makeSelectEntityTypeValue } from 'services/entityTypes/selectors';

type SpraySubmissionItem = ReturnType<typeof selectSubmissionItems>[0];

const selectSubmissionTableColumns = createDeepEqualSelector(
    [makeSelectTableColumnWidths(), makeSelectEntityTypeValue('pluralName')],
    (
        tableColumnWidths,
        entityTypeName: string
    ): VirtualizedTableColumns<SpraySubmissionItem> => {
        const result: VirtualizedTableColumns<SpraySubmissionItem> = [
            {
                label: 'Spray event id',
                value: 'eventId',
                width: 120,
                type: FIELD_TYPE_STRING,
                disableUpdating: true
            },
            {
                label: 'Date sprayed',
                value: 'sprayedAt',
                width: 150,
                type: FIELD_TYPE_DATE,
                disableUpdating: true
            },
            {
                label: 'Date submitted',
                value: 'submittedAt',
                width: 150,
                type: FIELD_TYPE_DATE,
                disableUpdating: true
            },
            {
                label: 'Spray product name',
                value: 'sprayProductName',
                width: 250,
                type: FIELD_TYPE_STRING,
                disableUpdating: true
            },
            {
                label: 'RPIN',
                value: 'rpin',
                width: 125,
                type: FIELD_TYPE_STRING,
                disableUpdating: true
            },
            {
                label: entityTypeName,
                value: 'sprayEntities',
                width: 250,
                type: FIELD_TYPE_STRING,
                disableUpdating: true
            },
            {
                label: 'Status',
                value: 'status',
                width: 100,
                type: FIELD_TYPE_STATUS_ICON,
                disableUpdating: true
            },
            {
                label: 'Final spray',
                value: 'isFinalSpray',
                width: 100,
                type: FIELD_TYPE_CONFIRMATION_CHECKBOX,
                disableUpdating: true
            },
            {
                label: 'Payload',
                value: 'payload',
                width: 125,
                type: FIELD_TYPE_STRING,
                disableUpdating: true,
                disableSearching: true
            },
            {
                label: 'Error message',
                value: 'errorMessage',
                width: 400,
                type: FIELD_TYPE_STRING,
                disableUpdating: true
            }
        ];

        return result.map((column) => {
            const { width, value } = column;
            return {
                ...column,
                width: get(tableColumnWidths, value, width)
            };
        });
    }
);

const selectSubmissionItems = createDeepEqualSelector(
    [selectSpraySubmissionArray, selectEntities, selectProductsKeyedById],
    (spraySubmissions, entities, products) =>
        spraySubmissions.map((spraySubmission) => {
            const {
                id,
                eventId,
                sprayProductId,
                submittedAt,
                failedAt,
                succeededAt,
                payload,
                sprayEntityIds,
                errorMessage
            } = spraySubmission;
            const { spray_date: sprayedAt, orchard_code: rpin } = payload;
            const sprayProductName = get(products, [sprayProductId, 'name']);
            const sprayEntities = sprayEntityIds
                .map((entityId) => get(entities, [entityId, 'name']))
                .sort()
                .join(', ');
            const isFinalSpray =
                get(payload, ['blocks', 0, 'varieties', 0, 'final_spray']) ===
                'Y';
            let status = IN_PROGRESS;

            if (failedAt) {
                status = ERROR;
            } else if (succeededAt) {
                status = SUCCESS;
            }

            return {
                key: id,
                eventId,
                sprayedAt,
                submittedAt,
                sprayProductName,
                sprayEntities,
                rpin,
                status,
                isFinalSpray,
                payload: id,
                errorMessage
            };
        })
);

const makeSelectSubmissionItems = (): typeof selectSubmissionItems =>
    selectSubmissionItems;

export {
    selectSubmissionTableColumns,
    selectSubmissionItems,
    makeSelectSubmissionItems
};
